/**
 * Self-serve demo tracking (ticket 4).
 *
 * Mints a stable anonymous id in localStorage the first time someone touches a
 * pre-signup surface, and posts named events against it. At signup Register.js
 * forwards that id, and the backend claims the demo row onto the new account
 * (Endpoints/DemoTracking.claimDemoSession) — which is what finally connects
 * "someone played with the demo on Tuesday" to "this account exists".
 *
 * Not to be confused with utils/demoStorage.js, which is an internal Synapse
 * demo MODE toggle (fake transcripts for sales demos) and has nothing to do
 * with prospect tracking.
 *
 * The marketing site is Webflow, i.e. a different origin. Everything here is
 * therefore written to work standalone: no auth, no app state, no imports from
 * the React app. See docs/proactive-bug-detection.md for the snippet to paste
 * into the Webflow page — it is this file's logic, inlined.
 */

const ANONYMOUS_ID_KEY = 'cpt-demo-anonymous-id';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

/**
 * Get (or mint) this browser's demo id.
 *
 * Returns null when storage is unavailable — Safari private mode, an embedded
 * webview, a user with cookies blocked. A prospect with no id is simply not
 * tracked; nothing downstream may treat null as an error.
 */
export function getDemoAnonymousId(create = true) {
  try {
    const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existing) return existing;
    if (!create) return null;

    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : // Fallback must still match the server's [A-Za-z0-9_-]{8,64} check.
          `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(ANONYMOUS_ID_KEY, id);
    return id;
  } catch (e) {
    return null;
  }
}

function readUtm() {
  const utm = {};
  try {
    const params = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) utm[key] = value;
    });
  } catch (e) {
    /* no query string worth having */
  }
  return utm;
}

/**
 * Record one demo event. Fire-and-forget by design: this is called from
 * marketing-facing UI, so it must never block a click, never throw, and never
 * surface an error to a prospect.
 *
 * Uses the PUBLIC endpoint with no session — there is no account yet.
 */
export function trackDemoEvent(apiBaseUrl, event, properties = {}) {
  const anonymousId = getDemoAnonymousId();
  if (!anonymousId) return Promise.resolve(false);

  const body = JSON.stringify({
    anonymousId,
    event,
    properties,
    landingUrl: window.location.href,
    referrer: document.referrer || null,
    utm: readUtm(),
    userAgent: navigator.userAgent,
  });

  // keepalive so the last event of a session (the one right before they
  // navigate away to sign up) actually leaves the browser.
  return fetch(`${apiBaseUrl.replace(/\/$/, '')}/v1/demo/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  })
    .then((response) => response.ok)
    .catch(() => false);
}

/**
 * Clear the id after a successful signup so a second person signing up on the
 * same machine (a shared clinic front desk is the normal case) doesn't get the
 * first person's demo trail attributed to them. The backend also refuses to
 * re-claim an already-claimed row; this is the near-side half of that.
 */
export function clearDemoAnonymousId() {
  try {
    localStorage.removeItem(ANONYMOUS_ID_KEY);
  } catch (e) {
    /* nothing to clear */
  }
}
